import {
    agendaMailFeedbackNegative,
    agendaMailFeedbackPositive,
    agendaMailFeedbackTitle,
    agendaMailProvideAdditionalFeedback,
} from './createAgendaMailInfoBarMessage.locstring.json';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import ItemViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemViewState';
import * as ReadInfoBarHandlers from 'owa-mail-reading-pane-store/lib/utils/infoBarHandlers';
import Item from 'owa-service/lib/contract/Item';
import Message from 'owa-service/lib/contract/Message';
import { logCalendarUsage } from 'owa-shared-analytics';
import {
    InfoBarCustomAction,
    InfoBarMessageColor,
    InfoBarMessageRank,
    InfoBarMessageSource,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';

const UserVoiceForumUrl: string = 'https://outlook.uservoice.com/forums/912550';

/**
 * Gets a boolean of whether the Agenda Mail info bar should be created
 * @param item the item
 * @return true if the info bar should be created, false otherwise
 */
function shouldCreateAgendaMailInfoBar(item: Item): boolean {
    return (
        isFeatureEnabled('cal-agendaMail-feedback') &&
        item &&
        item.ItemClass.indexOf('IPM.Note.Agenda') != -1
    );
}

/**
 * Logs the feedback provided and updates the info bar view state
 * @param isUseful If the feedback was useful or not
 * @param infoBarKey the key of info bar message
 * @param itemViewState the item view state
 */
function logFeedbackAndUpdateInfoBar(
    isUseful: boolean,
    infoBarKey: string,
    itemViewState: ItemViewState
) {
    const userFeedback: string = isUseful ? 'FeedbackPositive' : 'FeedbackNegative';

    logCalendarUsage('AgendaMailFeedback', [userFeedback]);

    const sendAdditionalFeedbackAction: InfoBarCustomAction = {
        action: () => {
            window.open(UserVoiceForumUrl, '_blank');
        },
        text: loc(agendaMailProvideAdditionalFeedback),
    };

    const infoBarMessageViewState: InfoBarMessageViewState = {
        key: infoBarKey,
        source: InfoBarMessageSource.ReadingPane,
        rank: InfoBarMessageRank.Info,
        color: InfoBarMessageColor.Grey,
        message: loc(agendaMailFeedbackTitle),
        messageParts: [loc(agendaMailFeedbackTitle), sendAdditionalFeedbackAction],
    };

    ReadInfoBarHandlers.infoBarAddHandler(itemViewState)(infoBarKey, () => infoBarMessageViewState);
}

/**
 * Create the Agenda Mail  info bar if necessary
 * @param message the message
 * @param key the key of info bar message
 * @param itemViewState the item view state
 * @return the Agenda Mail InfoBarMessageViewState
 */
export default function tryCreateAgendaMailInfoBarMessage(
    message: Message,
    key: string,
    itemViewState: ItemViewState
): InfoBarMessageViewState {
    if (!shouldCreateAgendaMailInfoBar(message)) {
        return null;
    }

    const usefulFeedbackAction: InfoBarCustomAction = {
        action: () => {
            logFeedbackAndUpdateInfoBar(true /* IsUseful */, key, itemViewState);
        },
        text: loc(agendaMailFeedbackPositive),
    };

    const notUsefulFeedbackAction: InfoBarCustomAction = {
        action: () => {
            logFeedbackAndUpdateInfoBar(false /* IsUseful */, key, itemViewState);
        },
        text: loc(agendaMailFeedbackNegative),
    };

    const infoBarMessageViewState: InfoBarMessageViewState = {
        key: key,
        source: InfoBarMessageSource.ReadingPane,
        rank: InfoBarMessageRank.Info,
        color: InfoBarMessageColor.Grey,
        message: loc(agendaMailFeedbackTitle),
        messageParts: [
            loc(agendaMailFeedbackTitle),
            usefulFeedbackAction,
            ' | ',
            notUsefulFeedbackAction,
        ],
    };

    return infoBarMessageViewState;
}
